import { FC, lazy, LazyExoticComponent } from 'react';
import { Language, TRANSLATIONS_MAP } from '../lib/i18n/translationMap';
import { INTL_APP_CODE, RTL_LANGUAGES, RU_APP_CODE } from './constants';
import { GeoSuggestion } from './types';

export const lazyComponent = (
    name: string,
    importer: Promise<Record<string, FC>>
): LazyExoticComponent<FC> =>
    lazy(async () => {
        const component = await importer;
        return { default: component[name] };
    });

interface GeoCoderFeautureMember {
    GeoObject: {
        Point: { pos: string };
        name: string;
        description: string;
        metaDataProperty: {
            GeocoderMetaData: {
                Address: {
                    country_code: string;
                    Components: [
                        {
                            kind: string;
                            name: string;
                        }
                    ];
                };
            };
        };
    };
}

interface ILangCodesv {
    [x: string]: string;
}
const convertLangCode = (lang: string): string => {
    const langCodes: ILangCodesv = {
        ru: 'ru_RU',
        en: 'en_GB',
        fr: 'fr_FR',
        pt: 'pt_PT',
        ar: 'ar_AE',
        es: 'es_ES',
    };
    if (langCodes[lang]) {
        return langCodes[lang];
    }
    return langCodes.ru;
};

export async function getGeoCode(address: string, lang: string): Promise<GeoSuggestion[]> {
    const langParam = convertLangCode(lang);
    const url = `https://geocode-maps.yandex.ru/1.x?apikey=fa5dfa37-08dc-46a1-a201-603fc2e6e27c&lang=${langParam}&format=json&geocode=${address}`;
    const response = await fetch(url, { cache: 'force-cache' });
    const data = await response.json();
    return data.response.GeoObjectCollection.featureMember.map((obj: GeoCoderFeautureMember) => {
        return {
            country:
                obj.GeoObject.metaDataProperty.GeocoderMetaData.Address.country_code.toLocaleLowerCase(),
            point: obj.GeoObject.Point.pos.split(' '),
            name: `${obj.GeoObject.description ? `${obj.GeoObject.description}, ` : ''}${
                obj.GeoObject.name
            }`,
            shortName: obj.GeoObject.name,
        };
    });
}

export const mockGetGeoCode: () => Promise<GeoSuggestion[]> = async () => [
    {
        country: 'ru',
        point: ['37.587093', '55.733974'],
        name: 'Москва, Россия, улица Льва Толстого, 16',
        shortName: 'улица Льва Толстого, 16',
    },
    {
        country: 'ru',
        point: ['36', '54'],
        name: 'Москва, Россия, улица Цандера, 3',
        shortName: 'улица Цандера, 3',
    },
];

export const convertToBase64 = async (file: File) => {
    const reader = new window.FileReader();
    const result = await new Promise((resolve) => {
        reader.onloadend = (e) => {
            if (!e?.target) return;
            resolve(e.target.result);
        };
        reader.readAsDataURL(file);
    });
    return result;
};

const getQueryParamsLang = (): string | undefined => {
    const queryParams = window.location.search.substring(1);
    if (!queryParams) return;
    const queryLangParams = queryParams
        .split('&')
        .filter((item) => item.toLocaleLowerCase().includes('lang'))?.[0];
    if (!queryLangParams) return;
    // eslint-disable-next-line consistent-return
    return queryLangParams.split('=')?.[1];
};

export const getSystemLanguage = () => {
    let lang = 'en';
    const queryLang = getQueryParamsLang();
    const localStorageLang = window.localStorage.getItem('lang');
    const systemLang = window.navigator.language;
    const availableLanguages = Object.keys(TRANSLATIONS_MAP);
    if (queryLang && availableLanguages.includes(queryLang)) {
        lang = queryLang;
    } else if (localStorageLang && availableLanguages.includes(localStorageLang)) {
        lang = localStorageLang;
    } else if (systemLang && availableLanguages.includes(systemLang)) {
        lang = systemLang;
    }
    return lang as Language;
};

const entityMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;',
};

export const escapeHtml = (str: string) => {
    return str.toString().replace(/[&<>"'`=/\\]/g, (s) => {
        return entityMap[s as keyof typeof entityMap];
    });
};

export const isRTLLangue = (language: Language) => RTL_LANGUAGES.includes(language);

export const getAppCodeFromUrl = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const app = urlParams.get('app');
    return app !== null && app !== RU_APP_CODE ? INTL_APP_CODE : RU_APP_CODE;
};
